import {
  Button,
  Grid,
  Switch,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DataGrid,
  GridAddIcon,
  type GridColDef,
  type GridRenderCellParams,
  type GridTreeNodeWithRender,
} from '@mui/x-data-grid';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { Link, NavLink, Outlet } from 'react-router';
import { useContext, useEffect, useState } from 'react';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import type { Campaign } from '../models/types';
import useDialog from '../hooks/useDialog';
import DeleteCampaignDialog from '../components/DeleteCampaignDialog';
import { AuthContext } from '../contexts/AuthContext';
import type { OverridableStringUnion } from '@mui/types';

export default function Campaigns() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const authCtx = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState<Campaign[]>([
    // {
    //   id: 1,
    //   merchant_id: 1,
    //   merchant_name: 'Starbucks',
    //   name: 'Christmas drinks promo',
    //   description: 'Get 50% off on all Christmas drinks.',
    //   end_date: '2026-12-25',
    //   voucher_lifespan_days: 90,
    //   minimum_spend: 10,
    //   affiliate_earning: 5,
    //   referee_benefit_type: 'amount',
    //   referee_benefit_description: 'Get $2 off on all Christmas drinks.',
    //   referee_benefit_amount: 2,
    //   terms_and_conditions:
    //     'Help promote starbucks by sharing your experience and affiliate link on your social media of choice. Tranding and the Brand reserves the right to disqualify any inappropriate or negative marketing. Tranding and the Brand reserve the right to change the terms and conditions and any time without prior notice.',
    //   voucher_terms_and_conditions: [
    //     {
    //       text: 'Vouchers can be redeemed at all Starbucks outlets islandwide.',
    //     },
    //     {
    //       text: 'Cannot be used in conjunction with any other discounts, offers, or vouchers by the Brand.',
    //     },
    //     {
    //       text: 'Vouchers are not exchangeable for cash or any other monetary equivalents.',
    //     },
    //   ],
    //   max_vouchers: 10,
    // },
  ]);

  const toggleCampaignStatus = async (
    merchantId: number,
    campaignId: number,
    active: boolean,
  ) => {
    backdrop.handleBackdropOpen();
    let message = 'Campaign has been updated.';
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const { data, error } = await supabase.functions.invoke(
        `campaigns/${campaignId}/status`,
        {
          method: 'PATCH',
          body: {
            merchantId: merchantId,
            campaignId: campaignId,
            active: active,
          },
        },
      );

      if (error) {
        throw error;
      }

      const updatedCampaign = data.data as Campaign;

      setCampaigns((old) => {
        const updatedCampaigns = structuredClone(old);
        const index = updatedCampaigns.findIndex(
          (campaign) =>
            campaign.merchant_id === merchantId && campaign.id === campaignId,
        );
        updatedCampaigns[index] = updatedCampaign;
        return updatedCampaigns;
      });
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      message = errorCast.message;
      severity = 'error';
    }

    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  const handleRemoveCampaign = (merchantId: number, campaignId: number) => {
    setCampaigns((old) =>
      [...old]
        .filter(
          // Keep every campaign except the one being deleted (matches BOTH
          // merchant_id and id). Negating that pair gives an OR here.
          (campaign) =>
            campaign.merchant_id !== merchantId || campaign.id !== campaignId,
        )
        .sort((a, b) => a.id - b.id),
    );
  };

  const columns: GridColDef<Campaign>[] = [
    ...(authCtx.userRole === 'tranding_admin'
      ? [
          {
            field: 'merchant_id',
            headerName: 'Action',
            width: 100,
            renderCell: (
              param: GridRenderCellParams<
                Campaign,
                any,
                any,
                GridTreeNodeWithRender
              >,
            ) => (
              <Button
                variant="outlined"
                onClick={() => {
                  dialog.handleDialogOpen(
                    <DeleteCampaignDialog
                      merchantId={param.value}
                      campaignId={param.row.id}
                      handleRemoveCampaign={handleRemoveCampaign}
                    />,
                    `Delete Campaign - ${param.row.name}`,
                  );
                }}>
                <DeleteIcon />
              </Button>
            ),
          },
          {
            field: '',
            headerName: 'Active',
            width: 100,
            renderCell: (
              param: GridRenderCellParams<
                Campaign,
                any,
                any,
                GridTreeNodeWithRender
              >,
            ) => (
              <Switch
                checked={param.row.status === 'active'}
                onChange={(ev, checked) => {
                  toggleCampaignStatus(
                    param.row.merchant_id,
                    param.row.id,
                    checked,
                  );
                }}
              />
            ),
          },
        ]
      : []),

    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      type: 'number',
    },
    {
      field: 'type',
      headerName: 'Type',
      type: 'string',
    },
    ...(authCtx.userRole === 'tranding_admin'
      ? [
          {
            field: 'merchant_name',
            headerName: 'Brand',
            width: 100,
          },
        ]
      : []),
    {
      field: 'name',
      headerName: 'Campaign Name',
      width: 200,
      ...(authCtx.userRole === 'tranding_admin' && {
        renderCell: (param) => (
          <Button
            component={NavLink}
            // Product campaigns edit in CreateNewProductCampaign; vouchers in
            // CreateNewCampaign.
            to={
              param.row.type === 'product'
                ? `./product/${param.row.id}`
                : `./${param.row.id}`
            }
            state={param.row}
            size="small"
            variant="outlined">
            {param.value}
          </Button>
        ),
      }),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      width: 100,
      type: 'string',
    },
    {
      field: 'voucher_lifespan_days',
      headerName: 'Voucher Lifespan',
      width: 120,
      type: 'number',
    },
    {
      field: 'max_vouchers',
      headerName: 'Max. Vouchers',
      width: 100,
      type: 'number',
    },
    {
      field: 'minimum_spend',
      headerName: 'Min. Spend',
      width: 100,
      type: 'number',
    },
    {
      field: 'affiliate_earning',
      headerName: 'Affiliate Earning',
      width: 100,
      type: 'number',
    },
    {
      field: 'referee_benefit_type',
      headerName: 'Referee Benefit Type',
      width: 100,
      type: 'string',
    },
    {
      field: 'referee_benefit_description',
      headerName: 'Referee Benefit Description',
      width: 200,
      type: 'string',
    },
    {
      field: 'referee_benefit_amount',
      headerName: 'Referee Benefit Amount',
      width: 150,
      type: 'number',
    },
  ];

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*, merchants(name)')
        .order('id', { ascending: true });

      if (error) {
        console.error('Error fetching campaigns:', error);
        throw error;
      }

      setCampaigns(
        data.map((el) => ({ ...el, merchant_name: el.merchants.name })),
      );
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title="Campaigns">
      {authCtx.userRole === 'tranding_admin' && (
        <Grid container spacing={2} mb={2}>
          <Button
            component={Link}
            to="./new"
            startIcon={<GridAddIcon />}
            size="small"
            variant="contained">
            New Voucher Campaign
          </Button>
          <Button
            component={Link}
            to="./new/product"
            startIcon={<GridAddIcon />}
            size="small"
            variant="contained">
            New Product Campaign
          </Button>
        </Grid>
      )}
      <DataGrid columns={columns} rows={campaigns} />
      <Outlet />
    </PageBaseLayout>
  );
}
